import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../../widgets/Head';
import Hero from '../../widgets/Hero';
import Summary from '../../widgets/Summary';
import ConnectStrip from '../../widgets/ConnectStrip';
import FAQ from '../../widgets/FAQ';
import { IndustryExpertise } from '../../widgets/IndustryExpertise';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Image from '../../widgets/Image';
import ColumnSummary from '../../widgets/ColumnSummary';

import * as css from './EnterpriseSoftwareDevelopment.m.css';

const consultingImg = require('../../assets/images/illustration/consulting.svg');
const softwareCycleImg = require('../../assets/images/illustration/software-cycle.svg');

const cloudAppImg = require('../../assets/images/icon-illustration/cloud-application.svg');
const customImg = require('../../assets/images/icon-illustration/custom-software.svg');
const webAppImg = require('../../assets/images/icon-illustration/web-application.svg');
const crossPlatformImg = require('../../assets/images/icon-illustration/cross-platform-development.svg');
const legacyImg = require('../../assets/images/icon-illustration/legacy-application-modernization.svg');
const enterpriseImg = require('../../assets/images/icon-illustration/enterprise-application-integration.svg');
const engineeringImg = require('../../assets/images/illustration/engineering.svg');
const analysisImg = require('../../assets/images/illustration/analysis.svg');

const factory = create();

export const EnterpriseSoftwareDevelopment = factory(function EnterpriseSoftwareDevelopment() {
	return (
		<div>
			<Head
				title="Enterprise Software Development Solutions | SitePen"
				description="Enterprise software development company building integrated solutions to improve processes, streamline operations, and empower business success."
			/>

			<Hero background="level2BlueGreen">
				{{
					xlTitle: (
						<div>
							Enterprise Software
							<br />
							Development
						</div>
					)
				}}
			</Hero>

			<Section type="leading">
				<ContentWrapper>
					<Summary reverse>
						{{
							title:
								'Meet Your Business Objectives with Our Custom Enterprise Solutions',
							blurb: (
								<virtual>
									We offer a range of services that center around the development
									of efficient and maintainable software. Our engineering team has
									honed its expertise in a wide variety of languages and
									frameworks including the latest trends in cloud-based
									application development. SitePen's design team specializes in
									the creation of user interfaces that are clear and easy to use.
									As a part of this service, we work with your users to learn how
									your business works. We identify and map your business processes
									to maximize the experience of all of the application's users.
									Underpinning our engineering and design teams are SitePen's
									project managers. Our project managers work to keep the
									development process on track. As the project progresses and
									changes are identified, our project managers keep everyone
									synchronized and working toward a common goal.
								</virtual>
							),
							image: <Image src={consultingImg} size="lg" width={400} height={342} />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<ColumnSummary>
						{{
							title: 'Enterprise Software Development Services',
							blurb: (
								<virtual>
									Enterprise software development encompasses a wide range of
									applications. Our team is ready to address the unique challenges
									across this broad spectrum.
								</virtual>
							),
							items: [
								{
									title: 'Cloud Application Development',
									blurb: (
										<virtual>
											Every enterprise-scale business is trying to find the
											most effective way to tap into the limitless potential
											of cloud-based applications and services. Our
											engineering team monitors these fast-moving technologies
											and is able to design cloud-deployed systems that are
											scalable and reliable.
										</virtual>
									),
									image: (
										<div classes={[css.imageWrapper]}>
											<Image
												src={cloudAppImg}
												align="center"
												width={155}
												height={133}
											/>
										</div>
									)
								},
								{
									title: 'Custom Software Development',
									blurb: (
										<virtual>
											Custom software is often the vehicle that supports the
											unique challenges of fulfilling your customer's desire
											to obtain your products or services. We will partner
											with you to learn how your business works, and then we
											will work with you to transform those processes into
											efficient and maintainable software.
										</virtual>
									),
									image: (
										<div classes={[css.imageWrapper]}>
											<Image
												src={customImg}
												align="center"
												width={152}
												height={132}
											/>
										</div>
									)
								},
								{
									title: 'Web Application Development',
									blurb: (
										<virtual>
											Web applications provide an effective way to deliver
											solutions for a wide variety of internal and external
											needs. Our web application development services go
											beyond merely modeling your end-user requirements. We
											consider factors such as expected traffic density and
											application complexity to deliver a right-sized system
											for your needs.
										</virtual>
									),
									image: (
										<div classes={[css.imageWrapper]}>
											<Image
												src={webAppImg}
												align="center"
												width={136}
												height={137}
											/>
										</div>
									)
								},
								{
									title: 'Cross-Platform Development',
									blurb: (
										<virtual>
											Your customers want to access your applications using a
											staggering variety of platforms, from mobile devices to
											smartwatches to web applications. SitePen's engineering
											team can create solutions that support all of these
											platforms, allowing your customers to use your software
											in a way that works best for them.
										</virtual>
									),
									image: (
										<div classes={[css.imageWrapper]}>
											<Image
												src={crossPlatformImg}
												align="center"
												width={152}
												height={132}
											/>
										</div>
									)
								},
								{
									title: 'Legacy Application Modernization',
									blurb: (
										<virtual>
											Nothing lasts forever, especially software developed in
											an ever-changing market. We understand the challenges
											and opportunities that legacy software modernization
											projects bring. We believe that most applications can
											have several years added to their valuable lifespans
											through careful analysis and strategic updates that keep
											most of the application intact while supporting your
											business objectives.
										</virtual>
									),
									image: (
										<div classes={[css.imageWrapper]}>
											<Image
												src={legacyImg}
												align="center"
												width={135}
												height={138}
											/>
										</div>
									)
								},
								{
									title: 'Enterprise Application Integration',
									blurb: (
										<virtual>
											Enterprise applications are almost always part of a
											software suite that work together to support a
											business's day-to-day operations. Whether your
											integration leverages microservices, enterprise service
											buses, a shared database, or a combination of
											technologies, we will work within your environment to
											provide solutions that smoothly integrate with your
											existing application landscape.
										</virtual>
									),
									image: (
										<div classes={[css.imageWrapper]}>
											<Image
												src={enterpriseImg}
												align="center"
												width={155}
												height={135}
											/>
										</div>
									)
								}
							]
						}}
					</ColumnSummary>
				</ContentWrapper>
			</Section>
			<Section>
				<IndustryExpertise />
			</Section>
			<Section shaded>
				<ContentWrapper>
					<Summary imageGutter="medium" paddingBottom="md">
						{{
							title: 'Comprehensive Enterprise Software Development',
							blurb: (
								<virtual>
									You know that enterprise software development involves more than
									just writing code and so do we. We will work with you to develop
									a holistic strategy to create a solution that exceeds your
									customers' expectations. Whether you have just a few notes
									written down during a meeting or a comprehensive development
									plan, we will work with you to understand your goals and develop
									a plan to achieve them.
								</virtual>
							),
							image: (
								<Image
									src={engineeringImg}
									lazy
									size="lg"
									width={400}
									height={351}
								/>
							)
						}}
					</Summary>
					<Summary reverse imageGutter="medium">
						{{
							title: 'Improve Efficiencies Through Digital Transformation',
							blurb: (
								<virtual>
									Enterprise software isn't just merely running applications in
									the cloud; it is a binary representation of your business. We
									can help you transform your business with efficient,
									maintainable, and easy-to-use software. Our user-experience
									architects will lead you through the modeling of your business
									processes, identifying critical success factors along the way.
									The engineering team will continue to refine the models as they
									build the applications to eliminate ambiguity and deliver a
									clear, understandable solution that is a digital reflection of
									your business. Additionally, since no business can survive
									without growing, your new software will be designed with growth
									in mind so that it can adapt and change with you.
								</virtual>
							),
							image: (
								<Image src={analysisImg} lazy size="md" width={352} height={322} />
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper size="small">
					<Summary stack>
						{{
							title: 'Our Enterprise Software Development Process',
							image: <Image src={softwareCycleImg} lazy />,
							blurb: (
								<virtual>
									Our development partnership with you starts the moment you reach
									out to us. Our solution architects will work with you to
									understand your goals and gather the background information that
									informs our entire development process. Our project management,
									senior designers, and technical leads will take this information
									and develop high-level project plans before you even see a
									contract. We work with you to ensure that we have a clear,
									achievable plan that we all agree upon. Throughout the
									development process, we stay in constant contact with you. As we
									all learn more about the project, we'll find areas that need to
									be refined and changed. We will work with you to integrate these
									changes into the plan so that the software reflects all of the
									lessons we learned during its development.
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ConnectStrip />
			</Section>
			<Section trailing>
				<FAQ title="Enterprise Application Development FAQs">
					{[
						{
							title: 'What is Enterprise Application Development?',
							content: (
								<p>
									Enterprise application development shares many characteristics
									with other software engineering efforts. The applications must
									be correct, performant, and maintainable while also being
									flexible enough to allow features to be altered or added as
									business objectives evolve. There are, however, some key
									differences that make enterprise applications especially
									challenging to create. They often work together to govern most,
									if not all, of the enterprise's business processes. Frequently
									dozens of individual applications must coordinate their efforts
									to support the enterprise's objectives. Enterprise applications
									also tend to have a more diverse team of stakeholders than other
									software. They each bring different, and often incompatible,
									goals. Integrating new applications into a complex ecosystem
									while balancing each stakeholder's needs requires a unique blend
									of soft and hard skills.
								</p>
							)
						},
						{
							title: 'What is the Enterprise Application Development Process?',
							content: (
								<p>
									Successfully developing enterprise applications requires a
									transparent process that allows software the steadily advance
									toward an (often shifting) set of objectives. Our process begins
									with the solution architects, where the broad scope of the
									application is discussed and many fixed-constraints, such as
									software languages and frameworks, are identified. As this
									discovery phase continues, the project management and
									engineering teams lend their voices and experience to ensure
									that we have a clear understanding of what is known about the
									project and what will have to be discovered as the project
									progresses. After the discovery phase is complete, the
									engineering team will iteratively develop the application. Our
									project managers oversee the development and ensure that the
									project's goals remain focused on your objectives as they become
									more evident. Throughout this phase, our team will stay in
									constant contact with you - keeping you apprised of our progress
									and refining the project's objectives. As the development phase
									comes to a close, we'll work with you through an intense quality
									assurance phase to build confidence that the new application
									will be robust, reliable, and valuable.
								</p>
							)
						},
						{
							title: 'What Enterprise Software Solutions Do You Offer?',
							content: (
								<p>
									Enterprise development is what we do. Everything we do from the
									training of our team to our open-source contributions is
									intended to enhance our ability to quickly and efficiently
									develop applications that are ready for the rigors of operating
									in harmony as a part of an enterprise's software ecosystem. We
									stand ready to build new applications that will serve existing
									markets or open new markets for you to reach your customers
									wherever they are and however they want to interact with you.
									Our extensive experience enables us to build powerful internal
									applications, codifying complex business rules into applications
									that are verified by comprehensive automated test suites. If
									your organization is modernizing its software systems, then our
									engineering team's in-depth knowledge base will serve you well.
									Our engineers have worked in many of the most popular languages
									and frameworks in the industry, both past and present.
								</p>
							)
						},
						{
							title: 'What Are The Challenges Of Enterprise Application Development?',
							content: (
								<virtual>
									<p>
										Like almost any product or service, today's enterprise
										applications are expected to work correctly and efficiently.
										Customers expect to have clear interfaces and seamless
										experiences. The challenge with enterprise development lies
										in the additional complexity involved in achieving these
										goals. As a single cog in a complex software machine,
										enterprise applications must seamlessly integrate with other
										applications. Ensuring that this communication happens
										securely while also decoupling the applications to allow
										each to be upgraded and modified independently requires
										singular focus and a high level of expertise.
									</p>
									<p>
										Enterprise applications also have a more diverse group of
										stakeholders than many other applications. In addition to
										end-users and enterprise leadership, enterprise software
										must often consider the requirements introduced by business
										units, infrastructure teams, security, and government
										regulations. Without careful design and analysis, the
										complex and often contradictory demands of these teams can
										compromise the software's efficacy, leading to a sub-optimal
										user experience and eroding the value that the application
										adds to the organization.
									</p>
								</virtual>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default EnterpriseSoftwareDevelopment;
